import { cn } from '../cn';

export interface PromoBannerProps {
  readonly eyebrow?: string | null;
  readonly headline: string;
  readonly body?: string | null;
  readonly ctaLabel?: string | null;
  readonly ctaHref?: string | null;
  /** Under the content: the carousel's slide dots. */
  readonly footer?: React.ReactNode;
  /**
   * Draws the button without making it a link, for the admin app's preview:
   * a portal path there would open an admin page instead.
   */
  readonly preview?: boolean;
  /** The carousel pauses on hover and focus; these pass through to the card. */
  readonly onMouseEnter?: () => void;
  readonly onMouseLeave?: () => void;
  readonly onFocusCapture?: () => void;
  readonly onBlurCapture?: () => void;
  readonly className?: string;
}

const CTA_CLASS = 'mt-4 inline-block rounded-portal-button bg-white px-4 py-2.5 text-portal-tiny font-bold text-brand';

/**
 * One Overview promo banner: the gradient card in the portal's brand colors
 * with an eyebrow, headline, body and one button. The client portal's
 * carousel and the admin app's preview both draw it, so what BCE previews is
 * what clients see (in BCE's colors in the admin app, the client's own in
 * their portal).
 *
 * An https button opens in a new tab, since it leaves the portal; a portal
 * path stays in the tab.
 *
 * @param props - The banner's words, its link, and an optional footer.
 * @returns The card.
 */
export function PromoBanner({
  eyebrow,
  headline,
  body,
  ctaLabel,
  ctaHref,
  footer,
  preview = false,
  onMouseEnter,
  onMouseLeave,
  onFocusCapture,
  onBlurCapture,
  className,
}: PromoBannerProps): React.ReactElement {
  const external = (ctaHref ?? '').startsWith('https://');
  return (
    <div
      className={cn('relative overflow-hidden rounded-portal-card p-6 text-white', className)}
      style={{
        background: 'linear-gradient(120deg, var(--portal-brand-primary), var(--portal-brand-sidebar))',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocusCapture={onFocusCapture}
      onBlurCapture={onBlurCapture}
    >
      {/* Announced as a whole when it changes, rather than word by word. */}
      <div aria-live="polite" aria-atomic="true">
        {eyebrow ? <p className="text-portal-micro font-extrabold tracking-[1.2px] text-white/70">{eyebrow}</p> : null}
        <h2 className="mt-1.5 text-[22px] tracking-[-0.5px]">{headline}</h2>
        {body ? <p className="mt-1.5 max-w-[75ch] text-portal-tiny leading-[1.6] text-white/85">{body}</p> : null}
      </div>

      {ctaLabel && ctaHref ? (
        preview ? (
          <span className={CTA_CLASS}>{ctaLabel} →</span>
        ) : (
          <a href={ctaHref} className={CTA_CLASS} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
            {ctaLabel} →
          </a>
        )
      ) : null}

      {footer}
    </div>
  );
}
